/**
 * Tour Step Definitions
 *
 * Two independent guided tours, each anchored to a specific page rather than
 * chained through a broader onboarding journey (2026-09-05: the Onboarding
 * Tour and Workflow Tour were retired -- both mostly re-narrated things the
 * AI conversation on the Research page already walks a new user through
 * step by step, e.g. which nav items exist and how to start a workflow.
 * That duplicate teaching surface had already gone stale multiple times
 * (dead anchors from past refactors, caught only via
 * trackTourStepAnchorMissing telemetry) without adding much the chat
 * doesn't already cover. The Results and Loading tours stayed: they teach
 * genuinely non-redundant content -- statistical concepts (Monte Carlo,
 * walk-forward, overfitting) and live-progress UI -- that the AI doesn't
 * narrate inline next to the relevant element unless asked.):
 *
 * 1. Loading Tour (job progress page) — fills the wait while a backtest runs.
 * 2. Results Tour (ResultsVisualization) — QUICK_TOUR_STEPS (4) or
 *    FULL_TOUR_STEPS (9), all analytics sections.
 *
 * Each step targets a section anchor or data-tour element and provides
 * educational content explaining what it does and why it matters. A step
 * whose anchor doesn't exist on the current page is filtered out by
 * filterAvailableSteps() below (and reported via trackTourStepAnchorMissing)
 * rather than breaking the tour -- but a step that's ALWAYS filtered out
 * because its target was renamed/removed in a later refactor is a silent
 * bug, not a feature; keep step selectors in sync with the pages they
 * target when those pages change shape.
 */
import type { DriveStep } from 'driver.js';
import { trackTourStepAnchorMissing } from '@/lib/analytics';

// ---------------------------------------------------------------------------
// Loading Tour — progress screen while backtest is running (3 steps)
// ---------------------------------------------------------------------------
export const LOADING_TOUR_STEPS: DriveStep[] = [
  {
    popover: {
      title: 'Your Backtest Is Running!',
      description:
        'Great job! Your backtest has been submitted and is now running. This screen shows real-time progress as the platform works through each analysis phase. Let\'s take a quick look at what\'s happening.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '[data-tour="progress-card"]',
    popover: {
      title: 'Live Progress Tracker',
      description:
        'This card shows real-time updates:<br><br>\u2022 <strong>Status</strong> — Running or Queued<br>\u2022 <strong>Phase</strong> — which part of the pipeline is active (Loading Data → GA Optimization → Monte Carlo → Walk-Forward → Final Analysis)<br>\u2022 <strong>Progress bar</strong> — overall completion percentage<br>\u2022 <strong>Elapsed time</strong> — how long the job has been running<br><br>Updates stream in via WebSocket, so you\'ll see changes in real time.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: '[data-tour="job-config-card"]',
    popover: {
      title: 'Your Configuration',
      description:
        'A summary of exactly what you submitted — asset, exchange, capital, date range, and optimization settings. Useful for confirming everything looks right.<br><br>Now sit back and wait for the backtest to finish. When it completes, we\'ll automatically start a guided tour of <strong>every section</strong> in the results. ☕',
      side: 'bottom',
      align: 'start',
    },
  },
];

// ---------------------------------------------------------------------------
// Quick Tour — 4 key stops for a fast overview
// (was 5 -- the "Key Performance Metrics" at-a-glance card grid this used to
// point at (`[data-tour="key-metrics"]`) no longer exists as a standalone
// element after a results-page refactor; its content didn't survive in a
// form worth re-targeting to, so the step was removed rather than pointed
// at the wrong section. Found via tour.step_anchor_missing telemetry
// showing this anchor missing on every Results tour run since 2026-06-27.)
// ---------------------------------------------------------------------------
export const QUICK_TOUR_STEPS: DriveStep[] = [
  {
    element: '#section-summary',
    popover: {
      title: 'Strategy Verdict',
      description:
        'This is the bottom line. The system evaluates your strategy across multiple dimensions — profitability, risk, statistical robustness — and gives you a clear verdict: Viable, Marginal, or Not Viable.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '#section-equity',
    popover: {
      title: 'Equity Curve',
      description:
        'The equity curve plots your portfolio value over time. Look for a smooth upward slope — sharp dips are drawdowns, and flat periods mean the strategy is idle. A good strategy recovers quickly from dips and trends upward consistently.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: '#section-montecarlo',
    popover: {
      title: 'Monte Carlo Stress Test',
      description:
        'Monte Carlo analysis reshuffles your trades thousands of times to test how robust your results are. If the 95% confidence lower bound is positive, your strategy likely has a real edge — not just luck. The fan chart shows the range of possible equity paths.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: '#section-trades',
    popover: {
      title: 'Trade Analysis',
      description:
        'Every completed trade is logged here with entry/exit times, prices, P&L, and the reason your strategy triggered the trade. You need at least 30 trades for statistically meaningful results. Check that your winning trades are larger than losing ones on average.',
      side: 'top',
      align: 'start',
    },
  },
];

// ---------------------------------------------------------------------------
// Full Tour — comprehensive walkthrough of every section
// ---------------------------------------------------------------------------
export const FULL_TOUR_STEPS: DriveStep[] = [
  // 1. Verdict
  {
    element: '#section-summary',
    popover: {
      title: 'Your Strategy Verdict',
      description:
        'This is the bottom line. The system evaluated your strategy across profitability, risk-adjusted returns, drawdown tolerance, and statistical robustness. <strong>Green = Viable</strong> (real edge detected), <strong>Yellow = Marginal</strong> (worth iterating), <strong>Red = Not Viable</strong> (likely not profitable). Let\'s break down each metric.',
      side: 'bottom',
      align: 'start',
    },
  },
  // 2. Config
  {
    element: '#section-config',
    popover: {
      title: 'How This Backtest Was Configured',
      description:
        'A record of exactly what you ran — the asset pair, date range, initial capital, exchange, and simulation settings. If genetic optimization was used, the best parameters it found are displayed here. Use this to reproduce or tweak your run.',
      side: 'bottom',
      align: 'start',
    },
  },
  // 3. IS vs OOS
  {
    element: '#section-oos',
    popover: {
      title: 'Overfitting Check: In-Sample vs Out-of-Sample',
      description:
        '<strong>This is the most critical section.</strong> The strategy was optimized on "in-sample" data, then tested on unseen "out-of-sample" data. Compare the two:<br><br>\u2022 <strong>Similar performance</strong> = strategy captures a real pattern<br>\u2022 <strong>OOS much worse</strong> = likely curve-fitted to historical noise — not a real edge<br><br>A large IS\u2192OOS degradation is the #1 red flag in quantitative trading.',
      side: 'top',
      align: 'start',
    },
  },
  // 4. Monte Carlo
  {
    element: '#section-montecarlo',
    popover: {
      title: 'Monte Carlo Stress Test',
      description:
        'This reshuffles your trades thousands of times to test robustness. Key things to check:<br><br>\u2022 <strong>95% CI lower bound</strong> — if positive, your edge is statistically robust<br>\u2022 <strong>P&L distribution</strong> — the range of possible outcomes<br>\u2022 <strong>Fan chart</strong> — spread of equity paths across simulations<br><br>If most simulations are profitable, your strategy isn\'t dependent on lucky trade ordering.',
      side: 'top',
      align: 'start',
    },
  },
  // 5. Trades
  {
    element: '#section-trades',
    popover: {
      title: 'Your Trade Log',
      description:
        'Every trade your strategy made — entry/exit times, prices, P&L, and why it triggered. Things to check:<br><br>\u2022 <strong>>30 trades</strong> needed for statistical significance<br>\u2022 <strong>Avg winner > avg loser</strong> on a per-trade basis<br>\u2022 <strong>Not concentrated</strong> — profits shouldn\'t depend on 1-2 lucky bets<br>\u2022 <strong>Distribution</strong> — look for consistency across trades',
      side: 'top',
      align: 'start',
    },
  },
  // 6. Equity Curve
  {
    element: '#section-equity',
    popover: {
      title: 'Equity & Drawdown Curves',
      description:
        'The equity curve shows your portfolio value over time — look for a smooth upward slope. The drawdown chart below it shows how deep and long each losing period was.<br><br>\u2022 <strong>Smooth uptrend</strong> = consistent returns<br>\u2022 <strong>Sharp dips</strong> = drawdowns (check recovery time)<br>\u2022 <strong>Flat periods</strong> = strategy idle, no trades<br>\u2022 <strong>Quick recovery</strong> from drawdowns indicates resilience',
      side: 'top',
      align: 'start',
    },
  },
  // 7. Stats
  {
    element: '#section-stats',
    popover: {
      title: 'Statistical Significance',
      description:
        'These tests tell you if your edge is real or random luck:<br><br>\u2022 <strong>p-value < 0.05</strong> = less than 5% chance results are due to luck<br>\u2022 <strong>t-statistic</strong> = how many standard deviations your mean return is from zero (higher = more significant)<br><br>If p-value is high (>0.1), your results may not be statistically meaningful even if profitable.',
      side: 'top',
      align: 'start',
    },
  },
  // 8. Walk-Forward
  {
    element: '#section-oos', // retargeted 2026-07-23 -- walk-forward timeline now lives in a tab inside this section, see comment below
    popover: {
      title: 'Walk-Forward Validation',
      description:
        'The gold standard for detecting overfitting. Data is split into multiple windows — the strategy is re-optimized on each "in-sample" window, then tested on the next unseen window. Click the <strong>Period timeline</strong> tab below to see it.<br><br>\u2022 <strong>Degradation ratio > 0.5</strong> = strategy generalizes well<br>\u2022 <strong>Below 0.5</strong> = RED FLAG — works on past data but fails on new data<br>\u2022 Look for <strong>consistent performance</strong> across all windows',
      side: 'top',
      align: 'start',
    },
  },
  // 9. GA
  {
    element: '#section-ga',
    popover: {
      title: 'Genetic Algorithm Results',
      description:
        'The GA evolved populations of parameter sets to find the best configuration. Check:<br><br>\u2022 <strong>Convergence chart</strong> — fitness should improve then plateau (stable solution)<br>\u2022 <strong>Sensitivity analysis</strong> — which parameters matter most vs. least<br>\u2022 <strong>Fitness landscape</strong> — heatmaps showing how parameter pairs interact<br><br>If the landscape is smooth, the optimum is robust. If it\'s noisy, results may be fragile.',
      side: 'top',
      align: 'start',
    },
  },
];

// ---------------------------------------------------------------------------
// Helper: keep all steps — if the target element isn't in the DOM, show as a
// centered (non-anchored) popover instead of removing the step entirely.
// ---------------------------------------------------------------------------
function filterAvailableSteps(steps: DriveStep[], journey: string): DriveStep[] {
  return steps.map((step) => {
    if (!step.element) return step; // already non-anchored
    const el = document.querySelector(step.element as string);
    if (el) return step; // element found — keep anchored
    // Element missing — convert to centered popover. Emit a telemetry signal so
    // we can detect when a DOM/route refactor silently breaks an anchor.
    trackTourStepAnchorMissing(journey, step.element as string, window.location.pathname);
    const { element: _el, ...rest } = step;
    return rest;
  });
}

export function getAvailableTourSteps(fullTour: boolean): DriveStep[] {
  return filterAvailableSteps(fullTour ? FULL_TOUR_STEPS : QUICK_TOUR_STEPS, 'results');
}

export function getAvailableLoadingSteps(): DriveStep[] {
  return filterAvailableSteps(LOADING_TOUR_STEPS, 'loading');
}
